/* SIZESOFTX
=========================================================================================
* Descripción: Estado de cuenta y pagos por tenant
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 11/09/2026 | Lic. José Luis Aragón Cervantes  | Creación
* ---------------------------------------------------------------------------------------
*/

import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SearchIcon from "@mui/icons-material/Search";
import { apiCorreoEstadoCuenta, apiEstadoCuenta, apiPagoRegistrar, apiTenantsList } from "../settings/supportApi";
import {
  EMAIL_REGEX,
  sanitizarEmail,
  sanitizarTexto,
  sanitizarMultilinea,
  validarSinInyeccion,
} from "../settings/inputSecurity";

const GUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

const EstadoCuentaPage = () => {
  const [tenantId, setTenantId] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [seleccion, setSeleccion] = useState("");
  const [busquedaOpen, setBusquedaOpen] = useState(false);
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);

  const [periodo, setPeriodo] = useState("");
  const [monto, setMonto] = useState("");
  const [metodo, setMetodo] = useState("TRANSFERENCIA");
  const [referencia, setReferencia] = useState("");
  const [observaciones, setObservaciones] = useState("");

  const [correoOpen, setCorreoOpen] = useState(false);
  const [correoTo, setCorreoTo] = useState("");
  const [correoWorking, setCorreoWorking] = useState(false);

  const cargar = async () => {
    if (!tenantId) {
      setError("Ingresa el Tenant ID o busca por razón social.");
      return;
    }
    setError("");
    setOk("");
    try {
      setData(await apiEstadoCuenta(tenantId));
    } catch (err) {
      setError(err.message);
    }
  };

  const seleccionarTenant = async (t) => {
    setBusquedaOpen(false);
    setTenantId(t.id);
    setSeleccion(`${t.razonSocial || ""} (${t.tenantKey || t.id})`.trim());
    setError("");
    setOk("");
    try {
      setData(await apiEstadoCuenta(t.id));
    } catch (err) {
      setError(err.message);
    }
  };

  const buscarTenant = async () => {
    const texto = sanitizarTexto(tenantId, 120).trim();
    if (!texto) {
      setError("Ingresa el Tenant ID o busca por razón social.");
      return;
    }
    const bloqueo = validarSinInyeccion([{ etiqueta: "Búsqueda", valor: tenantId }]);
    if (bloqueo) {
      setError(`Bloqueado por seguridad: ${bloqueo}`);
      return;
    }
    setError("");
    setOk("");
    setSeleccion("");
    if (GUID_RE.test(texto)) {
      await cargar();
      return;
    }
    setBuscando(true);
    try {
      const res = await apiTenantsList({ busqueda: texto, pagina: 1, tamano: 10 });
      const items = Array.isArray(res?.items) ? res.items : [];
      if (items.length === 0) {
        setError("No se encontraron tenants que coincidan con la búsqueda.");
        return;
      }
      setResultados(items);
      setBusquedaOpen(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setBuscando(false);
    }
  }; 

  const registrarPago = async () => {
    const periodoLimpio = sanitizarTexto(periodo, 20).replace(/\s+/g, "");
    const referenciaLimpia = sanitizarTexto(referencia, 100);
    const observacionesLimpias = sanitizarMultilinea(observaciones, 500);
    if (!tenantId || !periodoLimpio || !monto) {
      setError("Tenant, periodo y monto son requeridos.");
      return;
    }
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(periodoLimpio)) {
      setError("Periodo inválido. Usa formato YYYY-MM.");
      return;
    }
    const bloqueo = validarSinInyeccion([
      { etiqueta: "Periodo", valor: periodo },
      { etiqueta: "Referencia", valor: referencia },
      { etiqueta: "Observaciones", valor: observaciones },
    ]);
    if (bloqueo) {
      setError(`Bloqueado por seguridad: ${bloqueo}`);
      return;
    }
    setError("");
    setOk("");
    try {
      await apiPagoRegistrar(tenantId, {
        periodo: periodoLimpio,
        monto: Number(monto),
        metodo,
        referencia: referenciaLimpia || null,
        estatus: "REGISTRADO",
        observaciones: observacionesLimpias || null,
      });
      setOk("Pago registrado.");
      setPeriodo("");
      setMonto("");
      setReferencia("");
      setObservaciones("");
      await cargar();
    } catch (err) {
      setError(err.message);
    }
  };

  const fmt = (v) => (v ? new Date(v).toLocaleString("es-MX") : "—");

  const enviarCorreo = async () => {
    setError("");
    setOk("");
    const destino = sanitizarEmail(correoTo, 180);
    if (destino && !EMAIL_REGEX.test(destino)) {
      setError("Correo destinatario inválido.");
      return;
    }
    const bloqueo = validarSinInyeccion([{ etiqueta: "Destinatario", valor: correoTo }]);
    if (bloqueo) {
      setError(`Bloqueado por seguridad: ${bloqueo}`);
      return;
    }
    setCorreoWorking(true);
    try {
      const res = await apiCorreoEstadoCuenta(tenantId, {
        toEmail: destino || undefined,
      });
      setOk(res?.mensaje || "Correo de estado de cuenta enviado.");
      setCorreoOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setCorreoWorking(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Estado de cuenta y pagos
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {ok && (
        <Alert severity="success" onClose={() => setOk("")} sx={{ mb: 2 }}>
          {ok}
        </Alert>
      )}

      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Tenant ID o búsqueda"
            placeholder="GUID, razón social, tenant key o RFC"
            fullWidth
            size="small"
            value={tenantId}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !buscando && tenantId.trim()) {
                e.preventDefault();
                buscarTenant();
              }
            }}
            onChange={(e) => {
              setTenantId(e.target.value);
              setSeleccion("");
              setBusquedaOpen(false);
            }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            disabled={buscando || !tenantId}
            onClick={buscarTenant}
          >
            Buscar
          </Button>
        </Grid>
        <Grid item xs={12} md={2}>
          <Button
            variant="outlined"
            startIcon={<MailOutlineIcon />}
            disabled={!tenantId}
            onClick={() => {
              setCorreoTo("");
              setCorreoOpen(true);
            }}
          >
            Correo
          </Button>
        </Grid>
      </Grid>

      {seleccion && (
        <Alert severity="info" onClose={() => setSeleccion("")} sx={{ mb: 2 }}>
          Cliente: {seleccion}
        </Alert>
      )}

      <Dialog open={busquedaOpen} onClose={() => setBusquedaOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Resultados de la búsqueda</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selecciona el cliente correcto para consultar su estado de cuenta.
          </Typography>
          <Stack spacing={1}>
            {resultados.map((s) => (
              <Button
                key={s.id || s.tenantKey}
                variant="outlined"
                color="primary"
                size="small"
                sx={{ justifyContent: "flex-start", textTransform: "none", textAlign: "left" }}
                onClick={() => seleccionarTenant(s)}
              >
                {s.razonSocial || "Sin razón social"} · {s.nombreComercial || "—"} · {s.tenantKey || s.rfc || s.id}{" "}
                · {s.estatus}
              </Button>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBusquedaOpen(false)}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      {data && (
        <>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1">
              Estatus suscripción:{" "}
              <Chip size="small" label={data.estatusSuscripcion} color={data.diasRestantesSuscripcion < 15 ? "warning" : "success"} />
            </Typography>
            {data.diasRestantesSuscripcion !== null && (
              <Typography variant="body2" color="text.secondary">
                Días restantes: {data.diasRestantesSuscripcion}
              </Typography>
            )}
            {data.suscripcion && (
              <Typography variant="body2" color="text.secondary">
                Período {data.suscripcion.billingPeriod} · {fmt(data.suscripcion.startsAtUtc)} →{" "}
                {fmt(data.suscripcion.endsAtUtc)} · Renovación {fmt(data.suscripcion.nextBillingAtUtc)}
              </Typography>
            )}
            {data.suscripcion && data.suscripcion.plan && (
              <Typography variant="body2" color="text.secondary">
                Plan {data.suscripcion.plan.nombre} ({data.suscripcion.plan.planKey}) · Precio base ${" "}
                {Number(data.suscripcion.plan.precioBase).toLocaleString("es-MX")} {data.suscripcion.plan.moneda}
              </Typography>
            )}
            {data.vencimientosProximos && (
              <Typography variant="body2" color="error">
                Próximo cargo: {fmt(data.vencimientosProximos.nextBillingAtUtc)} · {data.vencimientosProximos.estatus}
              </Typography>
            )}
          </Box>

          <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
            {data.saldosComunicacion.length === 0 && <Typography color="text.secondary">Sin saldos.</Typography>}
            {data.saldosComunicacion.map((c) => (
              <Chip
                key={c.uId}
                label={`${c.canal}: saldo ${c.saldoConsumo} / límite ${c.limiteAlerta}`}
                variant="outlined"
                color={c.bajoSaldo ? "warning" : "default"}
              />
            ))}
          </Stack>

          <Typography variant="h6" gutterBottom>
            Pagos (total: ${Number(data.totalPagos).toLocaleString("es-MX")} · pendiente: $
            {Number(data.totalPendiente).toLocaleString("es-MX")})
          </Typography>
          <Box sx={{ mb: 3 }}>
            {data.pagos.length === 0 && <Typography color="text.secondary">Sin pagos.</Typography>}
            {data.pagos.map((p) => (
              <Typography key={p.uId} variant="body2">
                {p.periodo} · ${Number(p.monto).toLocaleString("es-MX")} · {p.metodo} · {p.referencia || "—"} ·{" "}
                {p.estatus} · {fmt(p.pagadoAtUtc)} · por {p.registradoPor}
              </Typography>
            ))}
          </Box>
        </>
      )}

      <Box component="section">
        <Typography variant="h6" gutterBottom>
          Registrar pago
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2}>
            <TextField
              label="Periodo (YYYY-MM)"
              fullWidth
              size="small"
              value={periodo}
              onChange={(e) => setPeriodo(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Monto"
              fullWidth
              size="small"
              type="number"
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Método</InputLabel>
              <Select value={metodo} label="Método" onChange={(e) => setMetodo(e.target.value)}>
                <MenuItem value="TRANSFERENCIA">Transferencia</MenuItem>
                <MenuItem value="TARJETA">Tarjeta</MenuItem>
                <MenuItem value="EFECTIVO">Efectivo</MenuItem>
                <MenuItem value="CHEQUE">Cheque</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Referencia (opcional)"
              fullWidth
              size="small"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Observaciones (opcional)"
              fullWidth
              size="small"
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button variant="contained" onClick={registrarPago}>
              Registrar pago
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Dialog open={correoOpen} onClose={() => setCorreoOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Correo con estado de cuenta</DialogTitle>
        <DialogContent>
          <TextField
            label="Destinatario"
            type="email"
            size="small"
            fullWidth
            value={correoTo}
            onChange={(e) => setCorreoTo(e.target.value)}
            placeholder="correo@ejemplo.com"
            sx={{ mt: 1 }}
          />
          <Typography variant="caption" color="text.secondary">
            Si se deja vacío se usará el correo predeterminado capturado al activar la licencia del tenant.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCorreoOpen(false)}>Cancelar</Button>
          <Button variant="contained" startIcon={<MailOutlineIcon />} onClick={enviarCorreo} disabled={correoWorking}>
            Enviar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EstadoCuentaPage;